/** Every user holds this role at minimum — it is the foundation for all others. */
export const FOUNDATION_ROLE = 'संस्कृत शिक्षार्थी' as const;

export const ROLE_OPTIONS = ['संस्कृत शिक्षार्थी', 'शिक्षार्थी', 'पुरोहित', 'यजमान', 'उत्साही', 'Shiksharthi / Learner', 'Educator', 'Service Provider', 'Service Consumer'] as const;

export type RoleName = (typeof ROLE_OPTIONS)[number];

export type RoleActivityMap = Record<RoleName, number>;

export type RoleAbility =
  | 'view_sangati'
  | 'view_curriculum'
  | 'view_journey'
  | 'view_studio'
  | 'view_careers'
  | 'view_wall_feed'
  | 'create_wall_feed_items'
  | 'review_segments'
  | 'publish_jobs'
  | 'book_dhanurveda';

export interface RoleDefinition {
  id: RoleName;
  labels: { hi: string; en: string };
  abilities: RoleAbility[];
}

export const ROLE_DEFINITIONS: RoleDefinition[] = [
  {
    id: 'संस्कृत शिक्षार्थी',
    labels: { hi: 'संस्कृत शिक्षार्थी', en: 'Sanskrit Learner' },
    abilities: ['view_sangati', 'view_curriculum', 'view_journey', 'view_studio', 'view_wall_feed'],
  },
  {
    id: 'शिक्षार्थी',
    labels: { hi: 'शिक्षार्थी', en: 'Shiksharthi / Learner' },
    abilities: ['view_sangati', 'view_curriculum', 'view_journey', 'view_studio', 'view_wall_feed'],
  },
  {
    id: 'पुरोहित',
    labels: { hi: 'पुरोहित', en: 'Purohita / Guide' },
    abilities: ['view_sangati', 'view_curriculum', 'view_journey', 'view_studio', 'view_wall_feed', 'review_segments', 'book_dhanurveda'],
  },
  {
    id: 'यजमान',
    labels: { hi: 'यजमान', en: 'Yajamaana / Host' },
    abilities: ['view_sangati', 'view_careers', 'view_wall_feed', 'publish_jobs', 'book_dhanurveda'],
  },
  {
    id: 'उत्साही',
    labels: { hi: 'उत्साही', en: 'Utsaahi / Enthusiast' },
    abilities: ['view_sangati', 'view_curriculum', 'view_journey', 'view_studio', 'view_careers', 'view_wall_feed', 'create_wall_feed_items'],
  },
  // Simplified basic roles (limited per requirements to: Shiksharthi / Learner, Educator, Service Provider, Service Consumer)
  {
    id: 'Shiksharthi / Learner',
    labels: { hi: 'शिक्षार्थी / Shiksharthi', en: 'Shiksharthi / Learner' },
    abilities: ['view_sangati', 'view_curriculum', 'view_journey', 'view_studio', 'view_wall_feed'],
  },
  {
    id: 'Educator',
    labels: { hi: 'शिक्षक / Educator', en: 'Educator' },
    abilities: ['view_sangati', 'view_curriculum', 'view_journey', 'view_studio', 'view_wall_feed', 'review_segments'],
  },
  {
    id: 'Service Provider',
    labels: { hi: 'सेवा प्रदाता / Service Provider', en: 'Service Provider' },
    abilities: ['view_sangati', 'view_curriculum', 'view_journey', 'view_studio', 'view_careers', 'view_wall_feed', 'create_wall_feed_items', 'publish_jobs'],
  },
  {
    id: 'Service Consumer',
    labels: { hi: 'सेवा उपभोक्ता / Service Consumer', en: 'Service Consumer' },
    abilities: ['view_sangati', 'view_curriculum', 'view_journey', 'view_studio', 'view_careers', 'view_wall_feed'],
  },
];

export const DEFAULT_ROLE_ACTIVITY: RoleActivityMap = {
  'संस्कृत शिक्षार्थी': 0,
  'शिक्षार्थी': 0,
  'पुरोहित': 0,
  'यजमान': 0,
  'उत्साही': 0,
  'Shiksharthi / Learner': 0,
  'Educator': 0,
  'Service Provider': 0,
  'Service Consumer': 0,
};

export function roleLabel(role: RoleName, locale: string): string {
  const definition = ROLE_DEFINITIONS.find((entry) => entry.id === role);
  if (!definition) return role;
  return locale === 'en' ? definition.labels.en : definition.labels.hi;
}

export function roleAbilities(role: RoleName): RoleAbility[] {
  return ROLE_DEFINITIONS.find((entry) => entry.id === role)?.abilities ?? [];
}

export function canRole(role: RoleName, ability: RoleAbility): boolean {
  return roleAbilities(role).includes(ability);
}

export function normalizeRoleActivity(value: unknown): RoleActivityMap {
  const source = typeof value === 'object' && value !== null ? (value as Record<string, unknown>) : {};
  return ROLE_OPTIONS.reduce((acc, role) => {
    const raw = source[role];
    const numeric = Number(raw);
    acc[role] = Number.isFinite(numeric) && numeric >= 0 ? Math.floor(numeric) : 0;
    return acc;
  }, { ...DEFAULT_ROLE_ACTIVITY });
}
